import React from 'react';
import { Sparkles, Zap, Crown, Check, ArrowRight } from 'lucide-react';
import { usePlan } from '../../hooks/usePlan';

const PLAN_VISUALS = {
  start: {
    icon: Check,
    accent: 'bg-slate-500/10 border-slate-500/30 text-slate-300',
    iconWrap: 'bg-slate-500/15 text-slate-300',
    pill: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  },
  grow: {
    icon: Sparkles,
    accent: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300',
    iconWrap: 'bg-cyan-500/15 text-cyan-300',
    pill: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  },
  drivepro: {
    icon: Crown,
    accent: 'bg-fuchsia-500/10 border-fuchsia-500/30 text-fuchsia-300',
    iconWrap: 'bg-fuchsia-500/15 text-fuchsia-300',
    pill: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30',
  },
};

// Curated feature → display label map (only the marketing-relevant ones)
const FEATURE_DISPLAY = [
  { flag: 'storefront_enabled', label: 'Dealer Storefront' },
  { flag: 'inventory_enabled', label: 'Inventory Management' },
  { flag: 'buyer_leads_enabled', label: 'Buyer Leads' },
  { flag: 'customers_enabled', label: 'Customers' },
  { flag: 'reels_enabled', label: 'AI Reels' },
  { flag: 'ai_caption_enabled', label: 'AI Captions' },
  { flag: 'branded_templates_enabled', label: 'Branded Templates' },
  { flag: 'marketplace_leads_enabled', label: 'Marketplace Leads' },
  { flag: 'whatsapp_automation_enabled', label: 'WhatsApp Automation' },
  { flag: 'followup_automation_enabled', label: 'Day-2 & Day-5 Follow-ups' },
  { flag: 'source_tracking_enabled', label: 'Source Tracking' },
];

const PlanInfoCard = () => {
  const { plan, loading } = usePlan();
  if (loading && !plan) {
    return (
      <div className="bg-slate-900/40 border border-slate-700/50 rounded-2xl p-6 animate-pulse h-48" />
    );
  }
  const planId = plan?.plan?.id || 'start';
  const visual = PLAN_VISUALS[planId] || PLAN_VISUALS.start;
  const Icon = visual.icon;
  const features = plan?.features || {};
  const enabled = FEATURE_DISPLAY.filter((f) => features[f.flag]);
  const locked = FEATURE_DISPLAY.filter((f) => !features[f.flag]);

  return (
    <div
      data-testid="plan-info-card"
      className="relative bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 overflow-hidden"
    >
      <div className={`absolute -top-16 -right-16 w-48 h-48 rounded-full ${visual.iconWrap.split(' ')[0]} opacity-20 blur-2xl pointer-events-none`} />

      <div className="relative flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-2xl ${visual.iconWrap} flex items-center justify-center`}>
            <Icon className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-medium">Your Plan</div>
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              <h3 className="text-2xl font-extrabold text-white tracking-tight">
                {plan?.plan?.label || 'Start'}
              </h3>
              <span className={`px-2.5 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider border ${visual.pill}`}>
                {plan?.plan?.positioning || 'Digital Presence'}
              </span>
            </div>
            <p className="text-sm text-slate-400 mt-1 max-w-md">{plan?.plan?.tagline || ''}</p>
          </div>
        </div>

        {planId !== 'drivepro' && (
          <a
            href="mailto:support@propitstop.in?subject=Plan Upgrade Inquiry"
            data-testid="plan-upgrade-link"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 hover:text-cyan-200 px-3 py-2 rounded-lg border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 transition-colors"
          >
            See upgrade options <ArrowRight className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Enabled / Locked features */}
      <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-green-400 font-bold mb-2">
            Enabled · {enabled.length}
          </div>
          <ul className="space-y-1.5">
            {enabled.map((f) => (
              <li
                key={f.flag}
                data-testid={`plan-feature-on-${f.flag}`}
                className="flex items-center gap-2 text-sm text-slate-200"
              >
                <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                <span>{f.label}</span>
              </li>
            ))}
          </ul>
        </div>
        {locked.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">
              Available with Upgrade · {locked.length}
            </div>
            <ul className="space-y-1.5">
              {locked.map((f) => (
                <li
                  key={f.flag}
                  data-testid={`plan-feature-off-${f.flag}`}
                  className="flex items-center gap-2 text-sm text-slate-500"
                >
                  <Zap className="w-3.5 h-3.5 text-amber-500/60 flex-shrink-0" />
                  <span>{f.label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanInfoCard;
